import React from 'react';
import { Link } from 'react-router-dom';
import { FaGraduationCap, FaBullhorn, FaUsers, FaChartLine, FaUniversity } from 'react-icons/fa';
import useAnalytics from '../../hooks/useAnalytics';

const RecruiterBanner = ({ className = '' }) => {
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

  const handleCtaClick = (label) => {
    trackEvent({
      category: 'Recruiter Banner',
      action: 'Click',
      label,
      value: 1,
    });
  };

  return (
    <div className={`w-full bg-gradient-to-br from-[#FFEEED] via-[#FFD6D4] to-[#FFEEED] text-[#1B222A] shadow-lg ${className}`}>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
        {/* Decorative background orbs */}
        <div className="pointer-events-none absolute -top-10 -left-10 w-56 h-56 rounded-full bg-white/40 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-16 -right-16 w-72 h-72 rounded-full bg-white/30 blur-3xl" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center px-2 py-1 bg-white text-[#DB4F40] text-[11px] font-bold rounded-full border border-[#DB4F40]/20">
                📢 For Universities & Professors
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white text-[#DB4F40] text-[11px] font-bold rounded-full border border-[#DB4F40]/20">
                🆓 Free Registration
              </span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#DB4F40] leading-tight">
              Publish Your PhD Opportunities on PhDScanner
            </h3>
            <p className="mt-3 text-sm sm:text-base text-[#1B222A] leading-relaxed max-w-2xl">
              Reach a global audience of motivated researchers. List your doctoral openings via our Recruiter Portal and boost visibility. Trusted by a growing community with <strong>30K LinkedIn followers</strong> and over <strong>400K monthly Google Analytics views</strong> on phdscanner.com. <strong>Registration is free.</strong>
            </p>

            {/* Highlights */}
            <div className="mt-4 flex flex-wrap items-center gap-3 text-[12px] text-[#1B222A]/80">
              <span className="inline-flex items-center gap-1 bg-white/80 px-3 py-1.5 rounded-full border border-[#DB4F40]/20">
                <FaUsers className="text-[#DB4F40]" /> Broad global reach
              </span>
              <span className="inline-flex items-center gap-1 bg-white/80 px-3 py-1.5 rounded-full border border-[#DB4F40]/20">
                <FaChartLine className="text-[#DB4F40]" /> High-intent traffic
              </span>
              <span className="inline-flex items-center gap-1 bg-white/80 px-3 py-1.5 rounded-full border border-[#DB4F40]/20">
                <FaGraduationCap className="text-[#DB4F40]" /> Academia-focused audience
              </span>
            </div>

            {/* CTA */}
            <div className="mt-6">
              <Link
                to="/university/signup"
                onClick={() => handleCtaClick('Go to UniversitySignup')}
                className="inline-flex items-center justify-center px-5 py-3 bg-[#DB4F40] text-white rounded-xl font-semibold text-sm hover:bg-[#C64537] transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                <FaBullhorn className="w-4 h-4 mr-2" />
                Publish Opportunities
              </Link>
            </div>
          </div>

          {/* Visual */}
          <div className="order-1 lg:order-2">
            <div className="relative mx-auto w-full max-w-sm aspect-square">
              <div className="absolute inset-0 rounded-3xl bg-white/70 backdrop-blur-sm border border-[#FFD6D4] shadow-xl" />
              <div className="absolute -inset-3 rounded-[2rem] border-2 border-[#FFD6D4]/60" />
              <div className="relative z-10 w-full h-full flex items-center justify-center p-10">
                <div className="w-full h-full rounded-2xl bg-gradient-to-br from-white to-[#FFF3F2] flex items-center justify-center">
                  <img
                    src="/phdscanner_logo.webp"
                    alt="PhDScanner"
                    className="w-40 h-40 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="hidden items-center justify-center">
                    <FaUniversity className="w-24 h-24 text-[#DB4F40]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruiterBanner;


